import { Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { legacyDeliveryStatus } from "../../../utils/order-delivery-status"
import { DeliveryStatusBadge } from "./DeliveryStatusBadge"
import { StatusBadge } from "./StatusBadge"

type Props = {
  order: {
    orderStatus?: string | null
    /** BE ORDER_DELIVERY_STATUS, read from the order's delivery notes (2026-11-04). */
    deliveryStatus?: string | null
    deliveryNote?: { deliveryNoteStatus?: string | null; sla?: unknown } | null
  }
}

/**
 * «Хүргэлтийн төлөв» of a Борлуулалт row: where the order is in delivery, not its order status (that one stays in
 * the «Захиалгын төлөв» column, identical to Худалдан авалт). Since 2026-11-04 the backend keeps the two apart: the
 * order status ends at «Баталгаажсан» and `deliveryStatus` is read from the delivery notes.
 *  - NOT_CREATED → «Хүргэлт үүсгээгүй» (orange: it can be created now; grey while APPROVED waits for the payment
 *    term)
 *  - CREATED → «Хүргэлт үүссэн» (a DRAFT note; the catalog name «Драфт» means nothing to a seller)
 *  - CANCELLED → «Хүргэлт цуцлагдсан» (BE delivery-note/create.ts refuses a second note for the order)
 *  - any other stage → the delivery-note catalog name and colour (Хуваарилсан, Ачилт дууссан, …) + SLA flag
 *  - null: before confirmation / rejected / cancelled before confirmation → «-» (no delivery stage yet)
 */
export function DeliveryProgressBadge({ order }: Props) {
  const { t } = useTranslation("supplierSales")
  const own = (code: string, label: string, color: string) => (
    <StatusBadge code={code} catalog={[{ code, name: label, color }]} />
  )
  const status = order.deliveryStatus !== undefined ? order.deliveryStatus : legacyDeliveryStatus(order)

  if (!status) return <Text fz={14} c="gray.6">-</Text>
  if (status === "NOT_CREATED") {
    const color = order.orderStatus === "AUTHORIZED" ? "#F08C00" : "#868E96"
    return own("NONE", t("sales.deliveryProgress.none"), color)
  }
  if (status === "CREATED") return own("DRAFT", t("sales.deliveryProgress.created"), "#727576")
  if (status === "CANCELLED") return own("CANCELLED", t("sales.deliveryProgress.cancelled"), "#E03131")
  return <DeliveryStatusBadge code={status} sla={order.deliveryNote?.sla as never} />
}
